import { supabase } from './supabase';

/**
 * Image Size Configuration - All images use 1:1 square ratio
 * Each size is optimized for specific use cases in the app
 */
export const IMAGE_SIZES = {
  // Small thumbnails for compact displays
  'recipe-card-small': { width: 150, height: 150 },        // Small recipe cards
  'meal-tracker': { width: 500, height: 500 },             // Meal tracker cards - UPGRADED to 500x500 for enhanced display quality
  
  // Medium thumbnails for standard displays  
  'recipe-card': { width: 500, height: 500 },              // Standard recipe cards (Dashboard, Recipes page) - UPGRADED for crisp display
  'meal-slot-mobile': { width: 200, height: 200 },         // WeeklyPlanner mobile meal slots
  'meal-slot-desktop': { width: 200, height: 200 },        // WeeklyPlanner desktop meal slots
  'horizontal-card': { width: 500, height: 500 },          // Mobile horizontal scroll cards - UPGRADED for crisp display
  
  // Large thumbnails for detailed displays
  'recipe-modal': { width: 400, height: 400 },             // Recipe modals (was 800x600, now square)
  'recipe-modal-large': { width: 500, height: 500 },       // Large modal displays
  
  // Extra large for high-resolution displays
  'recipe-hero': { width: 600, height: 600 },              // Hero images, detailed views
} as const;

export type ImageSizeKey = keyof typeof IMAGE_SIZES;

/**
 * Universal Image Optimization Function
 * Handles BOTH user-uploaded and AI-generated images with consistent sizing
 * Creates optimized versions for ALL image sources in the app
 */
export async function getUniversalOptimizedImageUrl(
  imageUrl: string | null | undefined,
  useCase: ImageSizeKey,
  options: {
    fallbackUrl?: string;
    quality?: number;
  } = {}
): Promise<string> {
  const {
    fallbackUrl = '/img/small 1.png',
    quality = 90
  } = options;

  // Return fallback if no image URL provided
  if (!imageUrl) {
    return fallbackUrl;
  }

  // Get size configuration for the use case
  const sizeConfig = IMAGE_SIZES[useCase];
  if (!sizeConfig) {
    console.warn(`Unknown image size use case: ${useCase}`);
    return imageUrl;
  }

  try {
    // Extract image path and determine bucket
    const { imagePath, bucketName } = extractImagePathAndBucket(imageUrl);
    
    if (bucketName === 'images') {
      // User-uploaded images: Create optimized version using custom resize service
      return await createOptimizedUserImage(imagePath, sizeConfig, quality);
    } else {
      // AI-generated images: Use Supabase Transform API (public bucket)
      const { data } = supabase.storage
        .from(bucketName)
        .getPublicUrl(imagePath, {
          transform: {
            width: sizeConfig.width,
            height: sizeConfig.height,
            resize: 'cover',
            quality
          }
        });
      return data.publicUrl;
    }
  } catch (error) {
    console.error('Failed to generate universal optimized image URL:', error);
    return fallbackUrl;
  }
}

/**
 * Creates optimized version of user-uploaded images
 * Downloads original, resizes, and uploads to a cache bucket or returns blob URL
 */
async function createOptimizedUserImage(
  imagePath: string, 
  sizeConfig: { width: number; height: number }, 
  quality: number
): Promise<string> {
  try {
    // Generate a cache key for this specific size/quality combination
    const cacheKey = `optimized_${sizeConfig.width}x${sizeConfig.height}_q${quality}_${imagePath.replace(/[^a-zA-Z0-9]/g, '_')}`;
    
    // Check if we already have a cached optimized version
    const cachedVersion = localStorage.getItem(cacheKey);
    if (cachedVersion) {
      return cachedVersion;
    }

    // Download the original image from private bucket
    const { data: originalData, error } = await supabase.storage
      .from('images')
      .download(imagePath);
    
    if (error || !originalData) {
      throw new Error(`Failed to download original image: ${error?.message}`);
    }

    // Create a canvas to resize the image
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    return new Promise((resolve) => {
      img.onload = () => {
        // Set canvas size to target dimensions
        canvas.width = sizeConfig.width;
        canvas.height = sizeConfig.height;
        
        // Calculate crop dimensions to maintain aspect ratio
        const scale = Math.max(sizeConfig.width / img.width, sizeConfig.height / img.height);
        const cropWidth = sizeConfig.width / scale;
        const cropHeight = sizeConfig.height / scale;
        const cropX = (img.width - cropWidth) / 2;
        const cropY = (img.height - cropHeight) / 2;
        
        // Draw and resize image
        ctx?.drawImage(
          img,
          cropX, cropY, cropWidth, cropHeight,  // Source crop
          0, 0, sizeConfig.width, sizeConfig.height  // Destination size
        );
        
        // Convert to blob URL with specified quality
        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            // Cache the blob URL for future use
            localStorage.setItem(cacheKey, blobUrl);
            resolve(blobUrl);
          } else {
            resolve('/food.jpg'); // Fallback
          }
        }, 'image/jpeg', quality / 100);
      };
      
      img.onerror = () => resolve('/food.jpg');
      img.src = URL.createObjectURL(originalData);
    });
  } catch (error) {
    console.error('Failed to create optimized user image:', error);
    // Fallback: Return signed URL for original image
    try {
      const { data } = await supabase.storage.from('images').createSignedUrl(imagePath, 86400);
      return data?.signedUrl || '/food.jpg';
    } catch {
      return '/food.jpg';
    }
  }
}

/**
 * Generates optimized image URL using Supabase Transform API
 * Enforces 1:1 square ratio for all images
 * Supports both 'recipe-images' and 'images' buckets
 */
export function getOptimizedImageUrl(
  imageUrl: string | null | undefined,
  useCase: ImageSizeKey,
  options: {
    fallbackUrl?: string;
    quality?: number;
  } = {}
): string {
  const {
    fallbackUrl = '/img/small 1.png',
    quality = 90
  } = options;

  // Return fallback if no image URL provided
  if (!imageUrl) {
    return fallbackUrl;
  }

  // Get size configuration for the use case
  const sizeConfig = IMAGE_SIZES[useCase];
  if (!sizeConfig) {
    console.warn(`Unknown image size use case: ${useCase}`);
    return imageUrl;
  }

  try {
    // Extract image path and determine bucket
    const { imagePath, bucketName } = extractImagePathAndBucket(imageUrl);
    
    // Generate optimized URL with square dimensions using detected bucket
    const { data } = supabase.storage
      .from(bucketName)
      .getPublicUrl(imagePath, {
        transform: {
          width: sizeConfig.width,
          height: sizeConfig.height,
          resize: 'cover',  // Ensures image fills square while maintaining aspect ratio
          quality
        }
      });

    return data.publicUrl;
  } catch (error) {
    console.error('Failed to generate optimized image URL:', error);
    // Fallback to original image or placeholder
    return imageUrl || fallbackUrl;
  }
}

/**
 * Extracts image path and determines bucket from full Supabase URL
 * Handles both full URLs and path-only strings
 * Supports both 'recipe-images' and 'images' buckets
 */
function extractImagePathAndBucket(imageUrl: string): { imagePath: string; bucketName: string } {
  try {
    // If it's already a path (not a full URL), determine bucket from path structure
    if (!imageUrl.startsWith('http')) {
      // Check path structure to determine bucket
      if (imageUrl.includes('/images/') || (!imageUrl.includes('/recipe-images/') && !imageUrl.includes('recipe-images/'))) {
        return { imagePath: imageUrl.replace(/^\/images\//, ''), bucketName: 'images' };
      } else {
        return { imagePath: imageUrl.replace(/^\/recipe-images\//, ''), bucketName: 'recipe-images' };
      }
    }

    // Parse full Supabase URL to extract path and bucket
    const url = new URL(imageUrl);
    const pathParts = url.pathname.split('/');
    
    // Look for known bucket names
    const recipeImagesIndex = pathParts.findIndex(part => part === 'recipe-images');
    const imagesIndex = pathParts.findIndex(part => part === 'images');
    
    if (recipeImagesIndex !== -1 && recipeImagesIndex < pathParts.length - 1) {
      return {
        imagePath: pathParts.slice(recipeImagesIndex + 1).join('/'),
        bucketName: 'recipe-images'
      };
    } else if (imagesIndex !== -1 && imagesIndex < pathParts.length - 1) {
      return {
        imagePath: pathParts.slice(imagesIndex + 1).join('/'),
        bucketName: 'images'
      };
    }
    
    // Fallback: assume it's a user-uploaded image (images bucket)
    return {
      imagePath: pathParts[pathParts.length - 1],
      bucketName: 'images'
    };
  } catch (error) {
    console.error('Failed to extract image path and bucket:', error);
    return { imagePath: imageUrl, bucketName: 'images' };
  }
}

/**
 * Gets the appropriate image size for a specific component context
 * Helps maintain consistency across similar components
 */
export function getImageSizeForContext(context: string): ImageSizeKey {
  const contextMap: Record<string, ImageSizeKey> = {
    // Dashboard contexts
    'dashboard-recipe-card': 'recipe-card',
    'dashboard-meal-card': 'meal-tracker',
    
    // Recipes page contexts
    'recipes-card': 'recipe-card',
    'recipes-horizontal': 'horizontal-card',
    
    // WeeklyPlanner contexts
    'weekly-planner-mobile': 'meal-slot-mobile',
    'weekly-planner-desktop': 'meal-slot-desktop',
    
    // Modal contexts
    'recipe-modal': 'recipe-modal',
    'recipe-modal-large': 'recipe-modal-large',
    
    // MealTracker contexts
    'meal-tracker-card': 'meal-tracker',
    
    // Generic contexts
    'small': 'recipe-card-small',
    'medium': 'recipe-card',
    'large': 'recipe-modal',
    'hero': 'recipe-hero'
  };

  return contextMap[context] || 'recipe-card'; // Default to standard recipe card size
}

/**
 * Legacy support: Maps old thumbnail URLs to new optimized system
 * Maintains backward compatibility with existing thumbnailUrl fields
 */
export function migrateToOptimizedUrl(
  recipe: { thumbnailUrl?: string; imageUrl?: string; image_url?: string },
  useCase: ImageSizeKey = 'recipe-card'
): string {
  // Priority: imageUrl > image_url > thumbnailUrl
  const sourceUrl = recipe.imageUrl || recipe.image_url || recipe.thumbnailUrl;
  
  if (!sourceUrl) {
    return '/img/small 1.png';
  }

  // If it's already an optimized URL (has transform parameters), use it
  if (sourceUrl.includes('width=') && sourceUrl.includes('height=')) {
    return sourceUrl;
  }

  // Generate new optimized URL
  return getOptimizedImageUrl(sourceUrl, useCase);
}

/**
 * Utility to check if current image size matches target use case
 * Helpful for debugging and validation
 */
export function validateImageSize(useCase: ImageSizeKey): { width: number; height: number; isSquare: boolean } {
  const size = IMAGE_SIZES[useCase];
  return {
    width: size.width,
    height: size.height,
    isSquare: size.width === size.height
  };
}

/**
 * Development helper: Lists all available image sizes
 */
export function listAvailableSizes(): Array<{ useCase: ImageSizeKey; dimensions: string; pixels: number }> {
  return Object.entries(IMAGE_SIZES).map(([useCase, size]) => ({
    useCase: useCase as ImageSizeKey,
    dimensions: `${size.width}x${size.height}`,
    pixels: size.width * size.height
  }));
} 